import { useState, useMemo } from "react";
import Layout from "@/components/layout/Layout";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, History, Loader2 } from "lucide-react";
import { useActivityLogs, type ActivityLogWithUser } from "@/lib/api";
import { useAgencyContext } from "@/lib/agencyContext";
import { formatDateTime } from "@/lib/formatters";
import {
  activityEntityTypeLabel,
  buildActivityDetailLines,
  formatActivityAction,
  formatActivityActor,
  isHiddenActivityLog,
} from "@/lib/activity-log-format";

function actionBadgeVariant(
  action: string,
): "default" | "secondary" | "destructive" | "outline" {
  switch (action.toLowerCase()) {
    case "created":
      return "default";
    case "deleted":
      return "destructive";
    case "updated":
      return "secondary";
    case "coi_request.sent":
    case "coi.sent":
      return "outline";
    default:
      return "outline";
  }
}

function actionBadgeClass(action: string): string {
  switch (action.toLowerCase()) {
    case "created":
      return "bg-green-100 text-green-800 hover:bg-green-100 border-green-200";
    case "updated":
      return "bg-blue-100 text-blue-800 hover:bg-blue-100 border-blue-200";
    case "deleted":
      return "bg-red-100 text-red-800 hover:bg-red-100 border-red-200";
    case "coi_request.sent":
    case "coi.sent":
      return "bg-indigo-100 text-indigo-800 hover:bg-indigo-100 border-indigo-200";
    default:
      return "";
  }
}

// CSR-1844 — mirrors PolicyActivitiesTab's collapse: first 3 change lines,
// then a per-row "Show N more" toggle.
const COLLAPSED_DETAIL_LINES = 3;

function ActivityDetailsCell({ log }: { log: ActivityLogWithUser }) {
  const [expanded, setExpanded] = useState(false);
  const lines = useMemo(() => buildActivityDetailLines(log), [log]);

  if (lines.length === 0) return <span>—</span>;

  const hasHiddenLines = lines.length > COLLAPSED_DETAIL_LINES;
  const visibleLines =
    expanded || !hasHiddenLines
      ? lines
      : lines.slice(0, COLLAPSED_DETAIL_LINES);

  return (
    <div className="space-y-0.5">
      <ul className="list-disc pl-4 space-y-0.5">
        {visibleLines.map((line, index) => (
          <li key={index} className="whitespace-pre-wrap break-words">
            {line}
          </li>
        ))}
      </ul>
      {hasHiddenLines && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="text-xs font-medium text-primary hover:underline cursor-pointer"
          data-testid={`activity-details-toggle-${log.id}`}
        >
          {expanded
            ? "Show less"
            : `Show ${lines.length - COLLAPSED_DETAIL_LINES} more`}
        </button>
      )}
    </div>
  );
}

export default function ActivityLogs() {
  const { selectedAgency, isAllAgencies } = useAgencyContext();
  const agencyId = isAllAgencies ? null : selectedAgency?.id;
  // CSR-1844 — internal rows are hidden client-side, so fetch a deeper window
  // to keep the visible list covering a useful time span.
  const { data: logs, isLoading } = useActivityLogs(agencyId, 1000);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 25;

  // CSR-1844 — spec hide-list applied before search and pagination, so counts
  // and pages derive from the visible set. Display-only: DB rows untouched.
  const visibleLogs = useMemo(
    () => (logs ?? []).filter((log) => !isHiddenActivityLog(log)),
    [logs],
  );

  const filteredLogs = useMemo(() => {
    if (!searchTerm.trim()) return visibleLogs;
    const term = searchTerm.toLowerCase();
    return visibleLogs.filter(
      (log) =>
        log.action?.toLowerCase().includes(term) ||
        log.entityType?.toLowerCase().includes(term) ||
        log.entityName?.toLowerCase().includes(term) ||
        log.details?.toLowerCase().includes(term) ||
        log.userName?.toLowerCase().includes(term) ||
        log.userEmail?.toLowerCase().includes(term),
    );
  }, [visibleLogs, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredLogs.length / pageSize));
  const paginatedLogs = filteredLogs.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  // Reset to first page when search changes
  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold font-heading tracking-tight">
            Activity Log
          </h1>
          <p className="text-muted-foreground mt-1">
            View system and user activity.
          </p>
        </div>

        {/* Search */}
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by action, type, name, or details..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-9"
            data-testid="input-activity-search"
          />
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : paginatedLogs.length > 0 ? (
          <>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date/Time</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Entity Type</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="whitespace-nowrap text-sm">
                        {formatDateTime(log.createdAt as unknown as string)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatActivityActor(log)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={actionBadgeVariant(log.action)}
                          className={actionBadgeClass(log.action)}
                        >
                          {formatActivityAction(log.action)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm capitalize">
                        {activityEntityTypeLabel(log)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {log.entityName || log.entityId}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[380px]">
                        <ActivityDetailsCell log={log} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border rounded-md">
                <p className="text-sm text-muted-foreground">
                  Showing {((currentPage - 1) * pageSize + 1).toLocaleString()}
                  &ndash;
                  {Math.min(
                    currentPage * pageSize,
                    filteredLogs.length,
                  ).toLocaleString()}{" "}
                  of {filteredLogs.length.toLocaleString()} logs
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    First
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm px-3">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    Last
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-12 text-center text-muted-foreground border rounded-md">
            <History className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
            <p className="text-lg mb-2">No activity logs found</p>
            <p className="text-sm">
              {searchTerm
                ? "Try adjusting your search terms."
                : "Activity will appear here as changes are made in the system."}
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}
